<script setup>
import {ErrorMessage, Field, Form} from 'vee-validate'
import {computed, reactive, ref} from 'vue'
import axios from 'axios'
import cookieHelper from '../cookie'
import useGlobalStore from '../../store/globalStore'
import {useFormSubmit} from '../../composables/useFormSubmit'

const props = defineProps({
  isMultiple: {
    type: Boolean,
    default: false,
  },
  isCareHome: {
    type: Boolean,
    default: false,
  },
  postUrl: {
    type: String,
    default: '',
  },
})

const schema = {
  first_name: 'required',
  last_name: 'required',
  email: 'required|email',
  phone_number: 'required|phoneNumber',
}

let isSubmitting = ref(false)
const confirmation = reactive({show: false, message: '', class: ''})
const globalStore = useGlobalStore()
const { hideModal, showServerSideValidateErrors, enquirySubmitDataLayer, sendFBLeadData } = useFormSubmit()

const onSubmit = async () => {
  isSubmitting.value = true
  confirmation.show = true
  confirmation.class = 'message--warning'
  confirmation.message = 'Submitting....'
  const form  = document.getElementById('careHomeEmailEnquiry');
  let formData = new FormData(form);
  window.grecaptcha.execute(document.getElementById('mySiteKeyVariable').value, {
    action: formData.get('captcha_action'),
  }).then((token) => {
    const submitButton = form.querySelector('.submit');
    submitButton.disabled = true;
    formData.set('g-recaptcha-response', token);
    axios.post('/enquiry-submitted', formData).then(function (response) {
      confirmation.class = 'message--success'
      confirmation.message = response.data.msg
      enquirySubmitDataLayer(form);
      sendFBLeadData(formData.get('subject'));
      let currentUrl = window.location.href;
      currentUrl = currentUrl.replace('#enquiry-submitted', '');
      history.pushState(null, null, `${currentUrl}#enquiry-submitted`);

      const modalTitle = globalStore.modal.title;
      if (cookieHelper.getCookie('careHomeSuccessInfo') === null) {
        globalStore.setModal(
          {
            name: 'careHomeEnquirySuccess',
            title: modalTitle,
            id: response.data.id
          }
        )
      } else {
        hideModal()
      }
    }).catch(error => {
      submitButton.disabled = false;
      showServerSideValidateErrors(error, isSubmitting, confirmation)
    });
  });
}

const subscribeLabel = computed(() => {
  if (props.isCareHome) {
    return 'I would like to receive care home related information from Village Guide'
  }

  return 'I would like to receive emails with village info, updates and more from Village Guide'
})

</script>

<template>
  <Form
    class="modal-form modal-form--2column"
    :validation-schema="schema"
    v-slot="{ errors }"
    @submit="onSubmit"
    id="careHomeEmailEnquiry"
  >
    <slot name="token"></slot>
    <div class="modal__village-list" v-html="globalStore.modal.title" v-if="props.isMultiple"></div>
    <div class="row">
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="name" class="sr-only">First name</label>
          <Field
            id="first_name"
            name="first_name"
            placeholder="First name *"
            class="form-control"
            :class="{'is-danger': errors['first_name'] }"
            tabindex="1"
            autofocus
            autocomplete="off"
          />
          <ErrorMessage name="first_name" class="is-danger" />
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="last_name" class="sr-only">Last name</label>
          <Field
            name="last_name"
            placeholder="Last name *"
            class="form-control"
            :class="{'is-danger': errors['last_name'] }"
            tabindex="2"
            id="last_name"
          />
          <ErrorMessage name="last_name" class="is-danger" />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="email" class="sr-only">Email</label>
          <Field
            tabindex="3"
            id="email"
            name="email"
            placeholder="Email *"
            class="form-control"
            :class="{'is-danger': errors['email'] }"
          />
          <ErrorMessage name="email" class="is-danger" />
        </div>
      </div>
      <div class="col-lg-6 col-md-12">
        <div class="form-group form-group--input">
          <label for="phone_number" class="sr-only">Phone</label>
          <Field
            tabindex="4"
            id="phone_number"
            name="phone_number"
            placeholder="Phone *"
            class="form-control"
            :class="{'is-danger': errors['phone_number'] }"
          />
          <ErrorMessage name="phone_number" class="is-danger" />
        </div>
      </div>
    </div>

    <div class="form-group form-group--input">
      <label for="message" class="sr-only">Message</label>
      <Field
        as="textarea"
        tabindex="9"
        id="message"
        rows="3"
        :placeholder="globalStore.modal.descriptionPlaceholder"
        class="form-control form-control--textarea"
        name="message"
      ></Field>
    </div>
    <div tabindex="11" class="checkbox-custom mt-4 mb-4" v-if="!globalStore.modal.customField === 'Check availability'">
      <input  type="checkbox" class="subscribe" id="subscribe" name="subscribe" value="1" checked>
      <label class="form-check-label" for="subscribe" v-html="subscribeLabel"></label>
    </div>
    <input type="hidden" name="captcha_action" value="care_home_enquiry">
    <transition name="fade">
      <div :class="confirmation.class" class="message" v-if="confirmation.show">{{ confirmation.message }}</div>
    </transition>

    <div class="row mt-4 mb-8">
      <div class="col-lg-6">
        <slot name="hiddenfields"></slot>
        <button tabindex="12" type="submit" class="btn btn--green btn--send btn--info-pack submit mb-0">
          Send
        </button>
      </div>
      <div class="col-lg-6">
        <a
          v-if="isCareHome"
          href="{{ url('/rest-home/aged-care-employment') }}"
          class="btn btn--job-enquiries ml-auto mt-4"
        >For job enquiries <span>click here</span>
        </a>
      </div>
    </div>
    <slot name="googlerecaptchatext"></slot>
  </Form>
</template>
